import { useState, useRef, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import api from "services/api";

import getQueryParams from "common/get-query-params";
import encodeQueryParams from "common/encode-query-params";
import serveRequestErrors from "common/serve-request-errors";
import serveRequestSuccess from "common/serve-request-success";

import CONSTANTS from "constants/constant";

import ActionEnum from "enum/action";

import ErrorResponseDto from "dto/services/error-response-dto";
import PageCareerDto, { PageCareerTableQueryParamsDto, PageCareerTableCreatePayloadDto, PageCareerTableUpdatePayloadDto } from "dto/pages/career/page-career-dto";

import Button from "components/app-button";
import AppHeading from "components/app-heading";
import CareerTable from "components/pages/page-career/app-career-table";

const PageCareer = () => {
	const [disableAddNew, setDisableAddNew] = useState(false);
	const [newRow, setNewRow] = useState<PageCareerTableCreatePayloadDto>();
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const [isFetching, setIsFetching] = useState(false);
	const [careerData, setCareerData] = useState<PageCareerDto>();
	const queryParams = useRef(getQueryParams<PageCareerTableQueryParamsDto>(searchParams, { page: 0 }));

	const onHandleGetCareer = useCallback(async () => {
		setIsFetching(true);

		try {
			const { page } = queryParams.current;
			const payload = { size: 10, page };
			const response = await api.get.career.careers(payload);

			setCareerData(response.data.data.list);
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		} finally {
			setIsFetching(false);
		}
	}, []);

	//prettier-ignore
	const onHandleAddCareer = useCallback(async (payload: PageCareerTableCreatePayloadDto) => {
		let response = null;

		try {
			await api.post.career.create(payload);

			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}

		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.CREATED, ActionEnum.Create);

			if (careerData && careerData?.numberOfElements === careerData.size) {
				queryParams.current.page = careerData.totalElements % careerData.size === 0 ? careerData.totalPages : careerData.totalPages - 1;
			}

			onHandleGetCareer();
		}
	}, [careerData, onHandleGetCareer]);

	//prettier-ignore
	const onUpdateCareers = useCallback(async (payload: PageCareerTableUpdatePayloadDto | string, page? : number) => {
		let response = null;
		
		try {
			if (typeof payload !== "string") {
				await api.put.career.update({ id: payload.id }, payload);
			} else {
				await api.put.career.delete({ id: payload });
			}

			response = true;
		} catch (error) {
			const err = error as Error | ErrorResponseDto;

			serveRequestErrors(err);
		}
		if (response) {
			serveRequestSuccess(CONSTANTS.MESSAGES.UPDATED, ActionEnum.Update);

			if (typeof page === "number") {
				queryParams.current.page = page;
			}

			onHandleGetCareer();
		}
	}, [onHandleGetCareer]);

	const onHandlePageChange = (page: number) => {
		queryParams.current.page = page;

		memoSetSearchParams.current(encodeQueryParams(queryParams.current));

		onHandleGetCareer();
	};

	const onHandleAddNew = () => {
		setDisableAddNew(true);
		setNewRow({ title: "", employmentType: "", description: "", document: "", displayStatus: "", status: "active", isHiring: true });
	};

	useEffect(() => {
		onHandleGetCareer();
	}, [onHandleGetCareer]);

	return (
		<div className="page-career">
			<div className="career">
				<AppHeading title="Career Opportunities">
					<AppHeading.Divider />
				</AppHeading>

				<div className="career__button">
					<Button label="Add New" className="addNewBtn" disabled={disableAddNew} onClick={onHandleAddNew} />
				</div>

				{/* prettier-ignore */}
				<CareerTable jobs={careerData?.content || []} setDisableAddNew={setDisableAddNew} newRow={newRow} setNewRow={setNewRow} onUpdateCareers={onUpdateCareers} onCreateCareer={onHandleAddCareer} onHandlePageChange={onHandlePageChange} fetching={isFetching} paginationTable={{currentPage: careerData?.number || 0, totalPages: careerData?.totalPages || 0, totalElements: careerData?.totalElements || 0, numberOfElements: careerData?.numberOfElements || 0, tableSize: careerData?.size || 10 }} />
			</div>
		</div>
	);
};

export default PageCareer;
